import { create } from "zustand";
import { persist, createJSONStorage } from "zustand/middleware";

// other = 「其他」：不提供載具/統編，送單時發票欄位全帶 null（開一般 B2C 發票）
export type KioskInvoiceType = "carrier" | "tax_id" | "donate" | "other";

/**
 * 結帳頁選定的付款方式（電子付款 feature flag 開啟時可選）。
 * cash_on_delivery＝貨到付款、monthly＝月結（後端依 cis.customer_type 決定，這裡僅為顯示與選擇）；
 * credit_card 帶已綁卡 id 與顯示標籤（末四碼）。
 */
export type PaymentSelection =
  | { type: "cash_on_delivery" }
  | { type: "monthly" }
  | { type: "credit_card"; paymentMethodId: string; label: string }
  | { type: "apple_pay" };

/**
 * Kiosk 流程的結帳偏好狀態（發票 / 聯絡電話 / 熟客旗標）。
 * 購物車品項、配送時段、優惠券沿用 useOrder；這裡只放 useOrder 沒有的欄位。
 * 用 sessionStorage 持久化，跨頁切換（home → cart → checkout → confirm）不遺失。
 */
interface KioskState {
  contactPhone: string;
  setContactPhone: (v: string) => void;

  invoiceType: KioskInvoiceType;
  invoiceCarrier: string;
  invoiceTax: string;
  setInvoice: (v: { invoiceType?: KioskInvoiceType; invoiceCarrier?: string; invoiceTax?: string }) => void;

  /**
   * 是否視為熟客（下單過一次）。true → 結帳只問配送時間，付款/發票沿用上次。
   * 來源：bootstrap last_order 非空、或一鍵重訂、或本 session 成功送出過一次。
   */
  firstCheckoutDone: boolean;
  setFirstCheckoutDone: (v: boolean) => void;

  /** 本 session 是否已完成結帳簡訊 OTP 驗證（首次結帳需驗，之後略過）。 */
  otpVerified: boolean;
  /**
   * 結算頁「是否必須驗 OTP」。
   *
   * 權威來源是後端 GET /customer/contexts 的 phone_verified
   *（記於 customer_group_binding.phone_verified_at），由 WaitingArrangementGate 取回後寫入這裡。
   * 不用純前端 session 旗標：關頁／換裝置／清 sessionStorage 都會失準，
   * 而這是「掃鋼瓶 barcode 免 OTP 綁定」唯一的冒名下單防線。
   *
   * 預設 false（不驗）：既有客戶已由 migration backfill 為已驗證，
   * 且尚未取回結果前不應把正常客戶擋在結帳外。
   */
  needCheckoutOtp: boolean;
  setNeedCheckoutOtp: (v: boolean) => void;
  /** 最近一次「已寄出」的 OTP 門號與時間（epoch ms）：供提早預寄與結算頁避免重複發送。 */
  otpSentPhone: string;
  otpSentAt: number;
  setOtpSent: (phone: string, at: number) => void;
  setOtpVerified: (v: boolean) => void;

  /** 入口掃描/拍照已擷取鋼瓶鐵牌（並送 AW）→ 結帳頁就不再要求拍鐵牌。 */
  plateCaptured: boolean;
  setPlateCaptured: (v: boolean) => void;

  /** 客戶表明家中沒有瓦斯桶 → 結帳頁不要求拍鐵牌。 */
  noCylinder: boolean;
  setNoCylinder: (v: boolean) => void;

  /** 營業客戶：一鍵下單全程不顯示價格（專員報價）。 */
  hidePrice: boolean;
  setHidePrice: (v: boolean) => void;

  /** 新客戶/未匹配到瓦斯行 → 到首頁下單前需先選瓦斯行。 */
  needSupplierSelect: boolean;
  setNeedSupplierSelect: (v: boolean) => void;

  /**
   * 既有客戶但未比對到資料（自述是客戶、忘記/未填登記電話）→ 以既有身分下單，
   * 但標記為「待瓦斯行核對」，不歸類為新客戶。結帳/首頁顯示提示，送出時帶旗標給後端人工對帳。
   */
  pendingReview: boolean;
  setPendingReview: (v: boolean) => void;

  /**
   * 進結帳時需再做一次既有客戶比對（入口未確認身分者：家用電話/新客戶）。
   * 有地址後由後端相似度比對：比對到 → 顯示客戶資料確認；比對不到 → 收家中鋼瓶數量+用途。
   * 入口已「是我」確認過的（掃碼/手機比對）不需要。
   */
  needIdentityCheck: boolean;
  setNeedIdentityCheck: (v: boolean) => void;

  /**
   * 使用者身分頁按鈕的直接自稱（AW 待審清單「身份狀態」欄，隨 walk-in body 送後端）：
   * true＝有叫過／我是這間客戶／確認卡「是就是這家」後按不是我；false＝第一次叫；
   * null＝尚未表達（確認卡的「不是我」不覆寫此值）。
   */
  claimedExisting: boolean | null;
  setClaimedExisting: (v: boolean | null) => void;

  /** 傳單「有叫過桶」但逐層比對不到 → 首頁顯示「沒有找到您」卡（加瓦斯行 LINE 人工核對）。 */
  notFound: boolean;
  setNotFound: (v: boolean) => void;

  /** 傳單新客戶：合約是否已簽（首頁點簽或按下單入口時簽）。未簽 → 首頁顯示簽署入口、擋下單入口。 */
  contractSigned: boolean;
  setContractSigned: (v: boolean) => void;

  /**
   * 是否顯示發票填寫（平台層級 supplier 設定 platform_setting.show_invoice，PW 後台控制）。
   * opt-in：未設定時為 false，需 PW 後台明確開啟。
   * false → 結帳跳過發票步驟、確認頁不顯示發票摘要，發票一律以 donate 送出。預設 true。
   */
  showInvoice: boolean;
  setShowInvoice: (v: boolean) => void;

  /** 月結客戶（cis.customer_type === 月結客戶）→ 付款方式顯示「月結」而非「貨到付款」。預設 false。 */
  isMonthly: boolean;
  setIsMonthly: (v: boolean) => void;

  /**
   * 電子付款開關（supplier platform_setting.enable_electronic_payment，PW 後台控制）。
   * false → 結帳無「怎麼付款？」步驟、確認頁不可選卡，一律貨到付款/月結。預設 false（安全預設）。
   */
  allowElectronicPay: boolean;
  setAllowElectronicPay: (v: boolean) => void;

  /**
   * 結帳頁選定的付款方式（電子付款 feature flag 開啟時可選）。
   * null＝沿用預設（月結客戶＝月結，其餘＝貨到付款）。送出成功後於 resetCheckout 清除。
   */
  selectedPayment: PaymentSelection | null;
  setSelectedPayment: (v: PaymentSelection | null) => void;

  /** 回首頁時重置結帳偏好（保留 firstCheckoutDone，對齊 proto5 resetAll 行為） */
  resetCheckout: () => void;

  /** 最近一次成功下單的結果（供獨立成功頁 /kiosk/success 讀取，重新整理不消失） */
  lastSuccess: KioskSuccess | null;
  setLastSuccess: (v: KioskSuccess | null) => void;

  /** onboarding 入口來源（掃 QR / 傳單），供新註冊客戶建立 API 的 entrySource 欄位。 */
  entrySource: "qr" | "flyer" | null;
  setEntrySource: (v: "qr" | "flyer" | null) => void;

  /** onboarding 入口網址帶的原始 shopCode（未必已解析出 supplier_id）。 */
  entryShopCode: string;
  setEntryShopCode: (v: string) => void;

  /** 由 entryShopCode 呼叫 GET /register/supplier-by-shop-code 解析出的真實 supplier_id。 */
  entrySupplierId: string;
  setEntrySupplierId: (v: string) => void;

  /** OTP 驗證票證（phone_verified_ticket）；由 NpOtp / OtpCard 呼叫真實 send/verify-phone-otp
   *  驗證成功後寫入，見 docs/impl-notes-fe-otp.md。 */
  phoneVerifiedTicket: string;
  setPhoneVerifiedTicket: (v: string) => void;

  /** 手機/地址精確比對 100% 命中既有客戶時記下的 cis_id，供 walk-in 綁定用（後端仍會重驗）。 */
  matchedCisId: string;
  setMatchedCisId: (v: string) => void;

  /** match/by-phone 回報的「帶入地址不完整」。「這是我」後須先補填地址才綁定。 */

  /**
   * onboarding 過程中收集到的市話/家用電話（可能多組，逗號分隔）。
   * 帶進 walk-in body 的 customer.landline，供後端 cascade（手機 → 市話+地址）比對用；
   * 不在前端自己判斷是否綁定。
   */
  entryLandline: string;
  setEntryLandline: (v: string) => void;
  /**
   * onboarding 地址步驟(NpAddress)使用者實際填/定位的整串地址。
   * 帶進 walk-in body 的 customer.address.full_address（新客戶必填）。
   * 用這個「持久」store 而非 chooseIdentity.choose_address —— 後者導航到首頁會被
   * /customer/contexts 重列洗掉（見 reference_identity_choose_address_loss）。
   */
  entryAddress: string;
  /** onboarding 地址頁「給配送員的指示」；隨 walk-in 送出，寫入兩張地址表的 extra。 */
  entryAddressNote: string;
  setEntryAddressNote: (v: string) => void;
  setEntryAddress: (v: string) => void;
  /**
   * 該瓦斯行的「鋼瓶掃描」開關（supplier.system_setting.cylinder_scan）。
   * 由 onboarding 解析瓦斯行時寫入（shopCode 查詢回傳 / QR 掃碼入口視同開啟）。
   * 完成加入設定的鐵牌拍照會據此決定「是否同步嘗試讀 barcode 比對客戶」。
   */
  entryCylinderScan: boolean;
  setEntryCylinderScan: (v: boolean) => void;
}

export interface KioskSuccess {
  orderNo: string; // 顯示用標籤，如「#4159…」或「訂單已建立」
  orderId: string;
  itemCount: number;
  total: number;
  timeText: string; // 配送時段描述
  paymentText: string; // 付款方式描述
  address: string; // 送達地址
  /** app_tracker：對應下單時 order_submit_click 的 client_request_id，讓成功頁的 order_success_view 能對照同一次送出。 */
  clientRequestId?: string;
}

export const useKiosk = create<KioskState>()(
  persist(
    (set) => ({
      contactPhone: "",
      setContactPhone: (v) => set({ contactPhone: v }),

      // 預設「其他」：免填出口，對應舊 donate 的角色（捐贈暫隱）
      invoiceType: "other",
      invoiceCarrier: "",
      invoiceTax: "",
      setInvoice: ({ invoiceType, invoiceCarrier, invoiceTax }) =>
        set((s) => ({
          invoiceType: invoiceType ?? s.invoiceType,
          invoiceCarrier: invoiceCarrier ?? s.invoiceCarrier,
          invoiceTax: invoiceTax ?? s.invoiceTax,
        })),

      firstCheckoutDone: false,
      setFirstCheckoutDone: (v) => set({ firstCheckoutDone: v }),

      otpVerified: false,
      needCheckoutOtp: false,
      setNeedCheckoutOtp: (v) => set({ needCheckoutOtp: v }),
      setOtpVerified: (v) => set({ otpVerified: v }),
      otpSentPhone: "",
      otpSentAt: 0,
      setOtpSent: (phone, at) => set({ otpSentPhone: phone, otpSentAt: at }),

      plateCaptured: false,
      setPlateCaptured: (v) => set({ plateCaptured: v }),

      noCylinder: false,
      setNoCylinder: (v) => set({ noCylinder: v }),

      hidePrice: false,
      setHidePrice: (v) => set({ hidePrice: v }),

      needSupplierSelect: false,
      setNeedSupplierSelect: (v) => set({ needSupplierSelect: v }),

      pendingReview: false,
      setPendingReview: (v) => set({ pendingReview: v }),

      needIdentityCheck: false,
      setNeedIdentityCheck: (v) => set({ needIdentityCheck: v }),

      notFound: false,
      setNotFound: (v) => set({ notFound: v }),

      claimedExisting: null,
      setClaimedExisting: (v) => set({ claimedExisting: v }),

      contractSigned: false,
      setContractSigned: (v) => set({ contractSigned: v }),

      showInvoice: false,
      setShowInvoice: (v) => set({ showInvoice: v }),
      allowElectronicPay: false,
      setAllowElectronicPay: (v) => set({ allowElectronicPay: v }),

      isMonthly: false,
      setIsMonthly: (v) => set({ isMonthly: v }),

      selectedPayment: null,
      setSelectedPayment: (v) => set({ selectedPayment: v }),

      // 下單成功後重置結帳狀態。OTP 相關一併清掉：
      // otpVerified / phoneVerifiedTicket / otpSent* 都是「這一次結帳」的產物，
      // 留著會讓下一筆訂單沿用上一次的驗證結果（票證也早已過期）。
      // needCheckoutOtp 不清——它由後端 phone_verified 決定，WaitingArrangementGate 會重新帶入。
      resetCheckout: () =>
        set({
          contactPhone: "",
          // 重置為免填的「其他」
          invoiceType: "other",
          invoiceCarrier: "",
          invoiceTax: "",
          otpVerified: false,
          phoneVerifiedTicket: "",
          otpSentPhone: "",
          otpSentAt: 0,
          selectedPayment: null,
        }),

      lastSuccess: null,
      setLastSuccess: (v) => set({ lastSuccess: v }),

      entrySource: null,
      setEntrySource: (v) => set({ entrySource: v }),

      entryShopCode: "",
      setEntryShopCode: (v) => set({ entryShopCode: v }),

      entrySupplierId: "",
      setEntrySupplierId: (v) => set({ entrySupplierId: v }),

      phoneVerifiedTicket: "",
      setPhoneVerifiedTicket: (v) => set({ phoneVerifiedTicket: v }),

      matchedCisId: "",
      setMatchedCisId: (v) => set({ matchedCisId: v }),


      entryLandline: "",
      setEntryLandline: (v) => set({ entryLandline: v }),

      entryAddress: "",
      setEntryAddress: (v) => set({ entryAddress: v }),
      entryAddressNote: "",
      setEntryAddressNote: (v) => set({ entryAddressNote: v }),

      entryCylinderScan: false,
      setEntryCylinderScan: (v) => set({ entryCylinderScan: v }),
    }),
    {
      name: "kioskCheckout",
      // v1：清除舊版殘留的 selectedPayment，讓付款方式不帶預設、每次重新選。
      version: 1,
      migrate: (persisted: any) => {
        if (persisted && typeof persisted === "object") delete persisted.selectedPayment;
        return persisted;
      },
      storage: createJSONStorage(() => sessionStorage),
    },
  ),
);
